<script>
  // Import Svelte
  import {
    onMount,
    onDestroy,
    createEventDispatcher,
    getContext
  } from "svelte";
  import { log, announce } from "./Logging.svelte";
  import { callActive, cameraActive, declineUntil, IN_CALL_DETECTION_INTERVAL_MS } from "../callState.js";
  import { gateMicrophone } from "../noiseGate.js";
  import { VENDOR } from "../vendorPaths.js";

  // Properties and defaults
  let componentName = "FaceDetection"; // Log name
  export let prefs;
  export let user;
  // True while the window must stay closed (do not disturb, or email not yet
  // confirmed). The camera still runs so the indicator is honest, but the device
  // never reports "available".
  export let blocked = false;
  const { makeAvailable, makeUnavailable } = getContext("statusUpdater");

  let faceDetectionLoaded = false;
  let present = false;
  const dispatch = createEventDispatcher();
  let lastDetection = Date.now() + -1 * 3600 * 1000; // Default to 1 hour ago
  let video = null;
  let faceDetected = false;
  let detectionHeight = 640;
  let detectionWidth = 480;
  let stream = null;
  // The camera stream as the browser gave it (its tracks are stopped on tear-down)
  // and the noise gate that sits on its microphone.
  let rawStream = null;
  let micGate = null;

  // Face detection normally runs in a worker (public/face-worker.js) so the
  // model's work never stalls the call video. There is no main-thread fallback:
  // the page's security policy allows no script evaluation outside the worker.
  const WORKER_URL = "/face-worker.js"; // scripts/stamp.js appends ?v=<hash>
  // Everything the model needs is served from this origin (see scripts/copy-vendor.js).
  const WORKER_URLS = {
    tf: VENDOR.tf,
    wasmBackend: VENDOR.wasmBackend,
    wasmBinary: VENDOR.wasmBinary,
    blazeface: VENDOR.blazeface,
    model: VENDOR.model
  };

  const WORKER_START_TIMEOUT_MS = 30000;
  let worker = null;
  let workerReady = false;
  let workerPending = {};
  let workerSeq = 0;
  // Chrome: the worker reads camera frames itself from a cloned track, so the page
  // never copies a frame. Elsewhere the page sends an ImageBitmap each cycle.
  let trackInWorker = false;
  let lastWorkerNote = null;
  let workerTrack = null; // cloned camera track whose frame stream the worker reads

  let pausedForCall = false;

  // How the detector reads the camera: "stream" (frames read inside the worker),
  // "copy" (the page copies frames from the preview element) or "pause" (the
  // detector leaves the camera alone while a window is open). Settings > Advanced.
  let activeMode = "stream";
  function currentMode() {
    return prefs.detectorPath || "stream";
  }

  // Move the detector to `mode`, idempotently.
  function applyMode(mode) {
    if (mode === activeMode) return;
    activeMode = mode;
    if (mode === "stream") {
      if (!trackInWorker) handOffTrack();
    } else {
      releaseTrack();
      if (mode === "copy" && video && stream && video.srcObject !== stream) {
        video.srcObject = stream; // the copy path reads frames from the preview element
        video.play().catch(() => {});
      }
    }
  }

  // Undo handOffTrack(): the worker stops reading and the cloned track is released.
  function releaseTrack() {
    if (worker) {
      try { worker.postMessage({ type: "stop" }); } catch (e) { /* gone */ }
    }
    if (workerTrack) {
      workerTrack.stop();
      workerTrack = null;
    }
    trackInWorker = false;
  }

  function handOffTrack() {
    if (activeMode !== "stream") {
      console.log("Detector frame path: " + activeMode + " (Settings > Advanced)");
      return;
    }
    if (!worker || !stream || typeof window.MediaStreamTrackProcessor !== "function") return;
    const source = stream.getVideoTracks()[0];
    if (!source) return;
    let clone = null;
    try {
      clone = source.clone();
      // A track itself cannot be transferred, but its frame stream can.
      const readable = new MediaStreamTrackProcessor({ track: clone }).readable;
      worker.postMessage({ type: "track", readable: readable }, [readable]);
      workerTrack = clone;
    } catch (err) {
      console.log("Camera frames cannot be streamed to the worker here; sending frames instead");
      if (clone) clone.stop();
    }
  }

  function workerSupported() {
    return (
      typeof window !== "undefined" &&
      typeof window.Worker === "function" &&
      typeof window.OffscreenCanvas === "function" &&
      typeof window.createImageBitmap === "function"
    );
  }

  function failPending(message) {
    const pending = workerPending;
    workerPending = {};
    Object.keys(pending).forEach(id => pending[id].reject(new Error(message)));
  }

  function startWorker(threshold) {
    return new Promise(resolve => {
      if (!workerSupported()) {
        console.log("Face detection worker unsupported here; detecting on the main thread");
        resolve(false);
        return;
      }
      let w;
      try {
        w = new Worker(WORKER_URL);
      } catch (err) {
        console.warn("Face detection worker could not start:", err && err.message ? err.message : err);
        resolve(false);
        return;
      }
      const timer = setTimeout(() => {
        console.warn("Face detection worker did not start in time");
        w.terminate();
        resolve(false);
      }, WORKER_START_TIMEOUT_MS);
      w.onerror = event => {
        console.warn("Face detection worker failed:", event && event.message ? event.message : event);
        clearTimeout(timer);
        workerReady = false;
        failPending("worker failed");
        w.terminate();
        if (worker === w) worker = null;
        resolve(false);
      };
      w.onmessage = event => {
        const m = event.data || {};
        if (m.type === "ready") {
          clearTimeout(timer);
          worker = w;
          workerReady = true;
          console.log("Face detection running in a worker (" + m.backend + " backend)");
          activeMode = prefs.detectorPath || "stream";
          if (activeMode === "stream") handOffTrack();
          else console.log("Detector frame path: " + activeMode + " (Settings > Advanced)");
          resolve(true);
          return;
        }
        if (m.type === "track-ready") {
          trackInWorker = true;
          if (!prefs.selfView && video) {
            // Nothing on the page needs the camera picture now. Playing it in a hidden
            // element keeps a second video player alive, and on macOS Chrome its frames
            // have been seen appearing inside the call video for an instant.
            try { video.pause(); } catch (e) { /* ignore */ }
            video.srcObject = null;
            console.log("Camera preview detached; frames go only to the detector");
          }
          console.log("Camera frames are read inside the worker; the page no longer copies frames");
          return;
        }
        if (!workerReady && m.type === "error") {
          // Failure while loading the runtime or model: give up on the worker.
          console.error("Face detection worker could not initialise:", m.message);
          clearTimeout(timer);
          w.terminate();
          resolve(false);
          return;
        }
        const p = workerPending[m.id];
        if (!p) return;
        delete workerPending[m.id];
        if (m.type === "error") p.reject(new Error(m.message));
        else {
          if (m.note && m.note !== lastWorkerNote) {
            lastWorkerNote = m.note;
            console.log("Face detector: " + m.note);
          }
          p.resolve(m);
        }
      };
      w.postMessage({ type: "init", threshold: threshold, urls: WORKER_URLS });
    });
  }

  async function detectInWorker() {
    const id = ++workerSeq;
    if (trackInWorker) {
      return new Promise((resolve, reject) => {
        workerPending[id] = { resolve: resolve, reject: reject };
        worker.postMessage({ type: "frame", id: id, width: detectionWidth, height: detectionHeight });
      });
    }
    // Right after the preview is re-attached (switching back to the copy path) the
    // element has no frame yet; copying it would throw, so report "not ready" instead.
    if (!video.videoWidth || video.readyState < 2) return { score: 0, note: "camera frame not ready" };
    // Let the browser scale the frame down as part of the copy: the detector only
    // needs detectionWidth x detectionHeight, and a smaller copy is a shorter stall.
    const vw = video.videoWidth || detectionWidth;
    const vh = video.videoHeight || detectionHeight;
    // Fit the frame into the detection box turned to the frame's orientation, so a
    // portrait camera (an iPad held upright) keeps its full height instead of being
    // shrunk to fit a landscape box.
    const portrait = vh > vw;
    const boxW = portrait ? Math.min(detectionWidth, detectionHeight) : Math.max(detectionWidth, detectionHeight);
    const boxH = portrait ? Math.max(detectionWidth, detectionHeight) : Math.min(detectionWidth, detectionHeight);
    const scale = Math.min(1, boxW / vw, boxH / vh);
    let bitmap;
    try {
      bitmap = await createImageBitmap(video, {
        resizeWidth: Math.max(1, Math.round(vw * scale)),
        resizeHeight: Math.max(1, Math.round(vh * scale)),
        resizeQuality: "low"
      });
    } catch (e) {
      bitmap = await createImageBitmap(video);
    }
    return new Promise((resolve, reject) => {
      workerPending[id] = { resolve: resolve, reject: reject };
      worker.postMessage(
        { type: "frame", id: id, bitmap: bitmap, width: detectionWidth, height: detectionHeight },
        [bitmap]
      );
    });
  }

  function stopWorker() {
    if (worker) {
      try {
        worker.postMessage({ type: "stop" });
      } catch (e) {
        // already gone
      }
      worker.terminate();
      worker = null;
    }
    trackInWorker = false;
    if (workerTrack) {
      workerTrack.stop();
      workerTrack = null;
    }
    workerReady = false;
    failPending("worker stopped");
  }
  // Start Video
  async function setupCamera() {
    let functionName = "setupCamera";
    video = document.getElementById("detectionVideo");
    // Set Video Constraints based on transmit resolution
    let videoConstraints = {};
    // A window is quiet most of the time. Automatic gain control turns a silent
    // room into a loud hiss (it keeps raising the microphone until it hears
    // something), and iOS cannot mute call audio fully, so it stays off; noise
    // suppression and echo cancellation stay on.
    let audioConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: false };
    videoConstraints.resizeMode = "crop-and-scale";
    if (prefs.transmitResolution == "3840x2160") {
      videoConstraints.width = 3840;
      videoConstraints.height = 2160;
    } else if (prefs.transmitResolution == "1920x1080") {
      videoConstraints.width = 1920;
      videoConstraints.height = 1080;
    } else if (prefs.transmitResolution == "1280x720") {
      videoConstraints.width = 1280;
      videoConstraints.height = 720;
    } else if (prefs.transmitResolution == "960x540") {
      videoConstraints.width = 960;
      videoConstraints.height = 540;
    } else if (prefs.transmitResolution == "640x480") {
      videoConstraints.width = 640;
      videoConstraints.height = 480;
    } else if (prefs.transmitResolution == "176x144") {
      videoConstraints.width = 176;
      videoConstraints.height = 144;
    }
    videoConstraints.frameRate = prefs.transmitFramerate;
    if (prefs.videoSource !== "auto") {
      videoConstraints.deviceId = { exact: prefs.videoSource };
    }
    if (prefs.audioSource !== "auto") {
      audioConstraints.deviceId = { exact: prefs.audioSource };
    }

    rawStream = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints,
      video: videoConstraints
    });
    // Windows get the microphone through a noise gate: silence until someone speaks.
    micGate = await gateMicrophone(rawStream);
    stream = micGate.stream;
    video.srcObject = stream;
    cameraActive.set(true);
    log(
      "Detection camera stream created with resolution = " +
        videoConstraints.width +
        "x" +
        videoConstraints.height +
        " and frameRate " +
        videoConstraints.frameRate,
      componentName,
      functionName,
      "info",
      "faceDetection"
    );

    log(
      "Detection frames are fitted into " +
        detectionWidth +
        "x" +
        detectionHeight +
        " and checked every " +
        prefs.faceDetectionInterval +
        " ms",
      componentName,
      functionName,
      "info",
      "faceDetection"
    );

    return new Promise(resolve => {
      video.onloadedmetadata = () => {
        resolve(video);
      };
    });
  }

  const renderPrediction = async () => {

    let functionName = "renderPrediction";
    let predictions = [];
    let inCallNow = false;
    callActive.subscribe(value => (inCallNow = value))();
    const mode = currentMode();
    if (workerReady) applyMode(mode);
    if (mode === "pause" && inCallNow) {
      // While a window is open the detector leaves the camera alone entirely and
      // the person is assumed present.
      if (!pausedForCall) {
        pausedForCall = true;
        console.log("Detector paused while the window is open");
      }
      predictions = [{ probability: 1 }];
    } else {
      if (pausedForCall) {
        pausedForCall = false;
        console.log("Detector resumed");
      }
      if (user && video && workerReady) {
        try {
          const result = await detectInWorker();
          predictions = result.score > 0 ? [{ probability: result.score }] : [];
        } catch (err) {
          console.warn("Face detection failed this cycle:", err && err.message ? err.message : err);
          predictions = [];
        }
      }
    }
    let timeSinceDetection = 0;
    let detectionScore = 0;
    let detected = false;

    let detection = null;
    if (predictions.length > 0) {
      detection = predictions[0];
      detected = true;
    }
    // While blocked (do not disturb, unconfirmed email) or after "Cancel" on an
    // opening window, report nobody here regardless of what the camera sees.
    let declined = false;
    const unsubscribeDecline = declineUntil.subscribe(until => (declined = Date.now() < until));
    unsubscribeDecline();
    if (detected && (blocked || declined)) {
      detected = false;
      detection = null;
      lastDetection = 0; // drop to unavailable at once, not after the slack period
      console.log(new Date().toISOString().slice(11, 19) + " face seen but window is " + (blocked ? "blocked" : "declined") + "; staying unavailable");
    }
    const stamp = new Date().toISOString().slice(11, 19) + " ";
    if (detected) {
      console.log(stamp + "face score " + Math.round(detection.probability * 100) + " (threshold " + prefs.minimumFaceScore + ")");
    } else {
      console.log(stamp + (lastDetection ? "no face for " + Math.round((Date.now() - lastDetection) / 1000) + " s" : "no face yet") + (trackInWorker ? " (frames read in worker)" : video ? " (camera " + video.videoWidth + "x" + video.videoHeight + ", paused " + video.paused + ")" : ""));
    }

    if (detected) {
      lastDetection = Date.now();
      detectionScore = Math.round(detection.probability * 100); //Present score as integer
      dispatch("debugStatus", {
        property: "Detection Score",
        value: detectionScore
      });
      dispatch("debugStatus", {
        property: "Detection Timer",
        value: timeSinceDetection
      });
      if (!faceDetected) {
        faceDetectionLoaded = true;
        faceDetected = true;
        console.log("presenceChange:", "available");
        present = true;
        makeAvailable();
        dispatch("debugStatus", {
          property: "Status",
          value: "available"
        });
      }
    } else {
      timeSinceDetection = Date.now() - lastDetection;
      dispatch("debugStatus", {
        property: "Detection Score",
        value: detectionScore
      });
      dispatch("debugStatus", {
        property: "Detection Timer",
        value: timeSinceDetection
      });
      if (faceDetected) {
        if (timeSinceDetection > prefs.faceDetectionSlack) {
          faceDetected = false;
          console.log("presenceChange:", "unavailable");
          present = false;
          makeUnavailable();
          dispatch("debugStatus", {
            property: "Status",
            value: "unavailable"
          });
        }
      }
    }
    let inCall = false;
    const unsubscribe = callActive.subscribe(value => (inCall = value));
    unsubscribe();
    const interval = inCall
      ? Math.max(prefs.faceDetectionInterval, IN_CALL_DETECTION_INTERVAL_MS)
      : prefs.faceDetectionInterval;
    await new Promise(resolve => setTimeout(resolve, interval));
    if (user && video) {
      requestAnimationFrame(renderPrediction);
    }
  };

  const setupPage = async () => {
    let functionName = "setupPage";
    log("Run", componentName, functionName, "info", "faceDetection");
    video = document.getElementById("detectionVideo");
    await setupCamera();
    video.play();
    let threshhold = prefs.minimumFaceScore / 100;
    const inWorker = await startWorker(threshhold);
    if (!inWorker) {
      // Detection runs only in the worker (the page's security policy allows no
      // script evaluation on the main thread). Keep the camera indicator honest and
      // say so; the device simply never becomes available.
      console.error("Face detection could not start in this browser; this device will not become available.");
      dispatch("debugStatus", { property: "Status", value: "detection unavailable" });
    }
    renderPrediction();
  };

  function stopVideo() {
    let functionName = "stopVideo";
    log("Run", componentName, functionName, "info", "faceDetection");
    // if (typeof video != "undefined") {
    //   const stream = video.srcObject;

    if (micGate) {
      micGate.stop();
      micGate = null;
    }
    if (stream) {
      log("Found stream", componentName, functionName, "info", "faceDetection");
      const tracks = [...stream.getTracks(), ...(rawStream ? rawStream.getTracks() : [])];

      tracks.forEach(function(track) {
        log("Kill track", componentName, functionName, "info", "faceDetection");
        track.stop();
      });
      video.srcObject = null;
      video = null;
      stream = null;
      rawStream = null;
    }
    cameraActive.set(false);
    // }
  }

  // Leaving the page must release the camera at once.
  function onPageHide() {
    destroyProcess();
  }

  function initProcess() {
    let functionName = "initProcess";
    log("Run", componentName, functionName, "info", "faceDetection");
    setupPage();
  }

  function destroyProcess() {
    let functionName = "destroyProcess";
    log("Run", componentName, functionName, "info", "faceDetection");
    stopWorker();
    stopVideo();
  }

  onMount(() => {
    announce("FaceDetection Mounted");
    faceDetectionLoaded = false;
    let functionName = "onMount";
    //Set detectionHeight and detectionWidth
    if (prefs.detectionResolution == "1920x1080") {
      detectionWidth = 1920;
      detectionHeight = 1080;
    } else if (prefs.detectionResolution == "1280x720") {
      detectionWidth = 1280;
      detectionHeight = 720;
    } else if (prefs.detectionResolution == "960x540") {
      detectionWidth = 960;
      detectionHeight = 540;
    } else if (prefs.detectionResolution == "640x480") {
      detectionWidth = 640;
      detectionHeight = 480;
    } else if (prefs.detectionResolution == "176x144") {
      detectionWidth = 176;
      detectionHeight = 144;
    }

    window.addEventListener("pagehide", onPageHide);
    if (user) {
      initProcess();
    } else {
      log(
        "User is not set",
        componentName,
        functionName,
        "verbose",
        "faceDetection"
      );
    }
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      destroyProcess();
    };
  });
</script>

<style>
  #detectionVideo {
    margin-top: auto;
    margin-left: 10px;
    margin-bottom: 10px;
  }
  /* With self view off the preview must not be painted at all. A hidden-but-painted
     video is still a compositor layer, and Chrome on macOS has been seen presenting
     its frames inside the call video for an instant (the "flicker"). visibility keeps
     the element playing, so face detection and the camera checks still work. */
  .hide-video {
    z-index: -99;
    visibility: hidden;
  }
</style>

{#if faceDetectionLoaded}
  <slot {present} {stream}/>
{/if}
<video
  class="is-overlay"
  class:hide-video={!prefs.selfView}
  id="detectionVideo"
  width="160"
  height="120"
  autoplay
  playsinline
  muted />
